import React from "react";
import {
  Box,
  Button,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import feedbackApi from "../api/feedbackApi";
import ModulePageShell from "../components/common/ModulePageShell";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import { PERFORMANCE_QUERY_KEYS } from "../constants/performanceConstants";
import usePerformancePermissions from "../hooks/usePerformancePermissions";
import usePerformanceUsers from "../hooks/usePerformanceUsers";

const FeedbackReceivedPage = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can, PERFORMANCE_PERMISSIONS } = usePerformancePermissions();
  const { userOptions, isLoading: usersLoading } = usePerformanceUsers();
  const [showGuide, setShowGuide] = React.useState(false);
  const [employeeId, setEmployeeId] = React.useState("");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const canReadFeedback = can(PERFORMANCE_PERMISSIONS.FEEDBACK_READ);

  if (!canReadFeedback) {
    return (
      <ModulePageShell
        title="Received Feedback"
        description="Track feedback received and acknowledge entries."
      >
        <EmptyState message="You do not have permission to view feedback." />
      </ModulePageShell>
    );
  }

  const listQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.feedback, "received", employeeId],
    queryFn: () =>
      feedbackApi.listFeedback({
        toEmployeeId: employeeId || undefined,
        page: 1,
        limit: 50,
      }),
  });

  const acknowledgeMutation = useMutation({
    mutationFn: feedbackApi.acknowledgeFeedback,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PERFORMANCE_QUERY_KEYS.feedback],
      });
      toast({
        title: "Feedback acknowledged",
        status: "success",
        duration: 2000,
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to acknowledge",
        description: error?.response?.data?.message || error.message,
        status: "error",
      });
    },
  });

  const rows = listQuery.data?.data || [];

  return (
    <ModulePageShell
      title="Received Feedback"
      description="Track feedback received and acknowledge entries."
      onRefresh={() => listQuery.refetch()}
      showGuide={showGuide}
      onToggleGuide={() => setShowGuide((prev) => !prev)}
    >
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={4}
      >
        <VStack align="stretch" spacing={3}>
          <Text fontWeight="semibold">Filter</Text>
          <Select
            placeholder="All employees"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            isDisabled={usersLoading}
          >
            {userOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </VStack>
      </Box>

      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={4}
      >
        {listQuery.isLoading ? (
          <LoadingState />
        ) : rows.length === 0 ? (
          <EmptyState message="No feedback records available." />
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>From</Th>
                <Th>Title</Th>
                <Th>Sentiment</Th>
                <Th>Date</Th>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((item) => (
                <Tr key={item._id}>
                  <Td>
                    {item.fromEmployeeId?.name || item.fromEmployeeId || "-"}
                  </Td>
                  <Td>{item.title || "-"}</Td>
                  <Td>{item.sentiment || "-"}</Td>
                  <Td>
                    {item.createdAt
                      ? new Date(item.createdAt).toLocaleDateString()
                      : "-"}
                  </Td>
                  <Td>
                    <Button
                      size="xs"
                      onClick={() => acknowledgeMutation.mutate(item._id)}
                      isDisabled={Boolean(item.acknowledgedAt)}
                      isLoading={acknowledgeMutation.isPending}
                    >
                      {item.acknowledgedAt ? "Acknowledged" : "Acknowledge"}
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </ModulePageShell>
  );
};

export default FeedbackReceivedPage;
